import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { metadataQuery } from '@@components/Page/loadContentDetail';
import { type ContentEntity } from '@@components/Page/types';
import {
    useContentResourcesLoaded,
    useContentResourcesStore,
} from '@@scripts/store/useContentResourcesStore';
import { parse } from '@@utils/idUtils';

export const idUtilsWithError = (id: string) => {
    let isError = false;
    let contentId = '';

    try {
        contentId = parse(id);
    } catch {
        isError = true;
    }

    // for this component, we only want to know if the id is parsable or not.
    // if the utility method is throwing an exception, we assume that id is malformatted.
    return {
        contentId,
        isError,
    };
};

type Props = {
    reactQueryOptions?: {
        refetchOnWindowFocus?: boolean;
    };
    contentQuery: {
        data?: { body: ContentEntity };
    };
};

/**
 * Puts the content & metadata the route's loader loaded into Zustand.
 *
 * Should be called at the top of the route/component that depends on content resources.
 * After this runs and `isLoading` is false, you can safely use `useLoadedMetadata()` etc.
 */
export const useDetailPageResourcesLoader = ({ reactQueryOptions, contentQuery }: Props) => {
    const setContent = useContentResourcesStore((state) => state.setContent);
    const setMetadata = useContentResourcesStore((state) => state.setMetadata);
    const clearContentResources = useContentResourcesStore((state) => state.clearContentResources);
    const isLoaded = useContentResourcesLoaded();

    const contentBody = contentQuery.data?.body;

    useEffect(() => {
        if (contentBody) {
            setContent(contentBody);
        }
    }, [setContent, contentBody]);

    const { data } = useQuery({
        ...metadataQuery(contentBody?.metadataId || 0),
        ...reactQueryOptions,
        enabled: Boolean(contentBody?.metadataId),
    });

    const metadata = data?.body;

    useEffect(() => {
        if (metadata) {
            setMetadata(metadata);
        }
    }, [metadata, setMetadata]);

    useEffect(
        () => () => {
            clearContentResources();
        },
        [clearContentResources],
    );

    return !isLoaded;
};
